import { useMemo } from 'react';
import getLang from 'I18n/utils/getLang';
import { LISTINGS_PER_PAGE } from '../constants';
import { mapAppliedFiltersForRpc, mapSortForRpc } from '../utils/searchMappers';
import { ASC, ORDERED_LISTING_ID, isAiQueryType } from '../utils/searchUtils';

/**
 * Props for transforming search parameters into backend-compatible format.
 * Used to handle different search types (LLM, ElasticSearch) and maintain
 * result ordering when needed.
 */

export function useSearchQueryTransformer({
  appliedFilters,
  cachedListingIds,
  page,
  queryType,
  shouldUseElasticSearch,
  sort
}) {
  return useMemo(() => {
    var _baseSearch$filter, _baseSearch$filter2, _baseSearch$filter3;
    // For pure AI searches, let backend handle everything
    if (isAiQueryType(queryType) && !shouldUseElasticSearch) {
      return {};
    }

    // Base search parameters for all non-pure-LLM searches
    const baseSearch = {
      filter: mapAppliedFiltersForRpc(appliedFilters),
      length: LISTINGS_PER_PAGE,
      offset: (page - 1) * LISTINGS_PER_PAGE,
      sorts: mapSortForRpc(sort) || [],
      language: getLang(),
      productTypes: [] // Required by SearchRequest type
    };

    // For regular ElasticSearch or when no cached IDs exist
    if (!shouldUseElasticSearch || !cachedListingIds.length) {
      return baseSearch;
    }

    // When transitioning from LLM to ElasticSearch:
    // 1. Create a filter to maintain reference to original LLM results
    // 2. This ensures we only show results from the original AI search
    const orderedListingIdFilterGroup = {
      clause: 'AND',
      filtersByField: {
        [ORDERED_LISTING_ID]: [{
          __typename: 'com.hubspot.marketplace.search.models.filters.LongFilterQuery',
          clause: 'OR',
          negation: false,
          values: cachedListingIds
        }]
      },
      negation: false
    };

    // Combine the ORDERED_LISTING_ID filter with user's filters
    // This ensures we maintain the original AI results while applying user's filters
    const updatedFilter = {
      clause: ((_baseSearch$filter = baseSearch.filter) === null || _baseSearch$filter === void 0 ? void 0 : _baseSearch$filter.clause) || 'AND',
      filterGroups: [orderedListingIdFilterGroup, ...(((_baseSearch$filter2 = baseSearch.filter) === null || _baseSearch$filter2 === void 0 ? void 0 : _baseSearch$filter2.filterGroups) || [])],
      negation: ((_baseSearch$filter3 = baseSearch.filter) === null || _baseSearch$filter3 === void 0 ? void 0 : _baseSearch$filter3.negation) || false
    };

    // Add ORDERED_LISTING_ID as primary sort to maintain sequence
    // while still allowing user's sort preferences
    const updatedSorts = [{
      field: ORDERED_LISTING_ID,
      order: ASC
    }, ...(baseSearch.sorts || [])];
    return Object.assign({}, baseSearch, {
      filter: updatedFilter,
      sorts: updatedSorts
    });
  }, [appliedFilters, cachedListingIds, page, queryType, shouldUseElasticSearch, sort]);
}